import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Container, Grid, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { School, Factory, LocalHospital, Domain, AccountBalance, Park } from '@mui/icons-material';
import SEO from '../components/SEO';

const AnimatedNumber = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const nodeRef = useRef(null);

  useEffect(() => {
    const end = parseInt(value.replace(/,/g, '').replace('+', ''));
    if (isNaN(end)) return;

    let startTime = null;
    let observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        const step = (timestamp) => {
          if (!startTime) startTime = timestamp;
          const progress = Math.min((timestamp - startTime) / duration, 1);
          setCount(Math.floor(progress * end));
          if (progress < 1) {
            window.requestAnimationFrame(step);
          } else {
            setCount(end);
          }
        };
        window.requestAnimationFrame(step);
        observer.disconnect();
      }
    });

    if (nodeRef.current) {
      observer.observe(nodeRef.current);
    }

    return () => observer.disconnect();
  }, [value, duration]);

  const displayString = value.includes(',') ? count.toLocaleString() : count.toString();
  return <span ref={nodeRef}>{displayString}{value.includes('+') ? '+' : ''}</span>;
};

function Home() {
  return (
    <Box sx={{ backgroundColor: '#f4f4f4' }}>
      <SEO 
        title="Home"
        description="Fem Tech Solution delivers innovative menstrual hygiene infrastructure, including smart sanitary napkin vending machines and incinerators, promoting safe and dignified solutions."
        keywords="fem tech solution, sanitary napkin machine, vending machine, safe disposal, hygiene solutions"
      />

      {/* Hero Section */}
      <Box sx={{
        background: 'linear-gradient(120deg, #ffffff 0%, #f3e5f5 100%)',
        pt: { xs: 14, md: 20 },
        pb: 12,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Circle Background */}
        <Box sx={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(126,87,194,0.05) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box className="fade-in-section is-visible">
                <Typography variant="h3" sx={{
                  color: '#4a148c', // Deep Purple
                  fontWeight: '800',
                  lineHeight: 1.2,
                  mb: 2,
                  letterSpacing: '-0.5px'
                }}>
                  Empowering Women Through <span style={{ color: '#7e57c2' }}>FemTech</span> Solutions
                </Typography>
                <Typography variant="h6" sx={{
                  color: '#666',
                  mb: 4,
                  fontWeight: '400',
                  maxWidth: '500px',
                  lineHeight: 1.6
                }}>
                  Safe, hygienic, and dignified menstrual health solutions designed for modern institutions.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    component={Link}
                    to="/contact"
                    size="large"
                    sx={{
                      background: 'linear-gradient(45deg, #7e57c2 30%, #5e35b1 90%)',
                      px: 5,
                      py: 1.5,
                      fontSize: '16px',
                      boxShadow: '0 10px 20px rgba(126, 87, 194, 0.3)',
                    }}
                  >
                    Partner With Us
                  </Button>
                  <Button
                    variant="outlined"
                    component={Link}
                    to="/services"
                    size="large"
                    sx={{
                      borderColor: '#7e57c2',
                      color: '#7e57c2',
                      px: 4,
                      py: 1.5,
                      fontSize: '16px',
                      borderWidth: '2px',
                      '&:hover': { borderWidth: '2px', backgroundColor: '#f3e5f5' }
                    }}
                  >
                    Learn More
                  </Button>
                </Box>
              </Box>
            </Grid>

            {/* Right Image */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 6, md: 0 } }}>
              <Box
                sx={{
                  animation: 'float 6s ease-in-out infinite',
                  position: 'relative'
                }}
              >
                <Box
                  component="img"
                  src={require('../images/vending/Safe Disposal Sys.jpeg')}
                  alt="Women Hygiene"
                  className="soft-shadow"
                  sx={{
                    width: '100%',
                    maxWidth: '420px',
                    height: 'auto',
                    borderRadius: '24px',
                    border: '8px solid rgba(255,255,255,0.8)',
                    objectFit: 'cover'
                  }}
                />
                {/* Floating Decor Card */}
                <Box sx={{
                  position: 'absolute',
                  bottom: '30px',
                  left: '-30px',
                  background: 'white',
                  padding: '15px 25px',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  display: { xs: 'none', md: 'block' }
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#7e57c2' }}>Trusted by 500+ Institutes</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* What We Do */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4" className="section-title" sx={{ color: '#333', fontWeight: 'bold' }}>
          WHAT WE DO
        </Typography>
        <Typography sx={{ fontSize: '16px', lineHeight: 1.8, color: '#555', mb: 2 }}>
          Fem Tech Solution delivers technology-driven menstrual hygiene solutions that improve access, dignity, and safety for women and girls across institutions and communities.
        </Typography>
        <Typography sx={{ fontSize: '16px', lineHeight: 1.8, color: '#555' }}>
          We focus on building practical infrastructure and awareness systems that ensure uninterrupted access to hygienic products in everyday spaces.
        </Typography>
      </Container>

      {/* Core Solutions - Modern UI */}
      <Box sx={{ backgroundColor: '#f9f9fc', py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{
            textAlign: 'center',
            mb: 8,
            fontWeight: 'bold',
            color: '#003366',
            letterSpacing: '0.5px',
            fontFamily: '"Roboto", sans-serif'
          }}>
            OUR SOLUTIONS
          </Typography>

          <Grid container spacing={5}>
            {[
              {
                title: 'Vending Solutions',
                img: require('../images/vending/Vending Solutions.jpeg'),
                desc: 'Easy access to hygienic sanitary products through smart vending machines.'
              },
              {
                title: 'Safe Disposal Systems',
                img: require('../images/vending/Safe Disposal Systems.png'),
                desc: 'Environment-friendly and safe disposal solutions ensuring dignity and cleanliness.'
              },
              {
                title: 'Awareness Programs',
                img: require('../images/vending/Awareness Programs.jpeg'),
                desc: 'Educational initiatives promoting menstrual health awareness and confidence.'
              }
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{
                  backgroundColor: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                  overflow: 'hidden',
                  height: '100%',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'translateY(-5px)' }
                }}>
                  {/* Image Container */}
                  <Box sx={{
                    height: '240px',
                    width: '100%',
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <Box
                      component="img"
                      src={item.img}
                      alt={item.title}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>

                  {/* Content */}
                  <Box sx={{ p: 4 }}>
                    <Typography variant="h6" sx={{
                      fontWeight: '600',
                      color: '#333',
                      mb: 2,
                      fontSize: '1.25rem'
                    }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{
                      color: '#666',
                      lineHeight: 1.6,
                      fontSize: '15px',
                      fontWeight: '400'
                    }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why FEM Tech / What Makes Us Different */}
      <Box sx={{ py: 12, backgroundColor: '#fcfcfc' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography variant="h6" sx={{ color: '#7e57c2', fontWeight: 'bold', mb: 1, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '14px' }}>
              Why Fem Tech Solution?
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#003366', textTransform: 'uppercase' }}>
              What Makes Us Different?
            </Typography>
            <Typography sx={{ color: '#666', mt: 3, maxWidth: '800px', mx: 'auto', fontSize: '16px', lineHeight: 1.8 }}>
              Fem Tech Solution is built on a simple belief: menstrual hygiene should be safe, accessible, and stigma-free. We combine smart technology with a women-centered approach to deliver real impact.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {[
              {
                id: '01',
                title: 'Women-Centered Solutions',
                desc: 'Our solutions are designed with the real needs of women and girls in mind, focusing on convenience, privacy, safety, accessibility, and dignity.'
              },
              {
                id: '02',
                title: 'Smart & Reliable Technology',
                desc: 'We provide modern menstrual hygiene solutions that help institutions simplify usage, maintenance, monitoring, and overall hygiene management.'
              },
              {
                id: '03',
                title: 'Sustainable Hygiene Practices',
                desc: 'We promote responsible menstrual waste management and environmentally conscious solutions that help organizations reduce their environmental impact.'
              },
              {
                id: '04',
                title: 'Designed for Institutions',
                desc: 'From educational institutions to corporate workplaces, hospitals, factories, and public facilities, our solutions can be adapted to different environments and user requirements.'
              },
              {
                id: '05',
                title: 'Complete Hygiene Support',
                desc: 'We aim to provide more than individual products. Our approach covers product supply, installation, awareness, maintenance, waste-management support, and long-term service.'
              },
              {
                id: '06',
                title: 'Focus on Accessibility',
                desc: 'Every woman deserves access to essential menstrual hygiene facilities. We work to make these facilities more accessible in everyday institutional spaces.'
              },
              {
                id: '07',
                title: 'Quality & Trust',
                desc: 'We focus on dependable products, professional service, transparent communication, and solutions designed for long-term institutional use.'
              },
              {
                id: '08',
                title: 'Awareness & Social Impact',
                desc: 'Technology becomes meaningful when it creates positive change. We support organizations in building awareness around menstrual hygiene and creating more comfortable environments where menstruation can be discussed without stigma.'
              }
            ].map((feature, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{
                  backgroundColor: '#fff',
                  p: 3,
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                  border: '1px solid #f0f0f0',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.4s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 12px 25px rgba(126, 87, 194, 0.08)',
                    borderColor: '#e1bee7'
                  },
                  '&:hover .feature-id': {
                    color: '#7e57c2',
                    opacity: 0.12,
                    transform: 'scale(1.1) translate(-8px, 8px)'
                  }
                }}>
                  <Typography className="feature-id" sx={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    fontSize: '60px',
                    fontWeight: '900',
                    color: '#003366',
                    opacity: 0.04,
                    transition: 'all 0.5s ease',
                    lineHeight: 1,
                    zIndex: 0
                  }}>
                    {feature.id}
                  </Typography>
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 1.5, fontSize: '15px', lineHeight: 1.4 }}>
                      {feature.title}
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '13px', lineHeight: 1.6 }}>
                      {feature.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{
            mt: 10,
            p: { xs: 4, md: 6 },
            backgroundColor: '#003366',
            borderRadius: '24px',
            boxShadow: '0 20px 40px rgba(0, 51, 102, 0.15)',
            maxWidth: '1000px',
            mx: 'auto',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <Typography sx={{
                position: 'absolute',
                top: '-20px',
                left: '20px',
                fontSize: '120px',
                color: 'rgba(255, 255, 255, 0.05)',
                fontFamily: 'serif',
                lineHeight: 1,
                zIndex: 0
            }}>
              "
            </Typography>
            <Typography sx={{ 
              color: '#ffffff', 
              fontWeight: '400', 
              fontSize: { xs: '20px', md: '28px' }, 
              lineHeight: 1.6, 
              position: 'relative',
              zIndex: 1,
              fontStyle: 'italic',
              letterSpacing: '0.5px'
            }}>
              "We are not just equipment providers. We are long-term partners in building sustainable hygiene systems."
            </Typography>
            <Box sx={{ width: '60px', height: '4px', backgroundColor: '#7e57c2', mt: 4, mb: 2, position: 'relative', zIndex: 1, borderRadius: '2px' }} />
            <Typography sx={{ color: '#e0e0e0', fontWeight: 'bold', letterSpacing: '2px', textTransform: 'uppercase', fontSize: '13px', position: 'relative', zIndex: 1 }}>
              Fem Tech Solution
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Where Used */}
      <Box sx={{ backgroundColor: '#003366', py: 10, color: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ mb: 6, fontWeight: 'bold', textAlign: 'center' }}>
            WHERE OUR SOLUTIONS ARE USED
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {[
              { name: 'Schools & Colleges', icon: <School fontSize="large" sx={{ mb: 1, opacity: 0.9 }} /> },
              { name: 'Factories & Workspaces', icon: <Factory fontSize="large" sx={{ mb: 1, opacity: 0.9 }} /> },
              { name: 'Hospitals & Clinics', icon: <LocalHospital fontSize="large" sx={{ mb: 1, opacity: 0.9 }} /> },
              { name: 'Hostels & Residential Institutions', icon: <Domain fontSize="large" sx={{ mb: 1, opacity: 0.9 }} /> },
              { name: 'Government Buildings', icon: <AccountBalance fontSize="large" sx={{ mb: 1, opacity: 0.9 }} /> },
              { name: 'Public Spaces & Community Centers', icon: <Park fontSize="large" sx={{ mb: 1, opacity: 0.9 }} /> }
            ].map((place, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box sx={{
                  p: 3,
                  border: '1px solid rgba(255,255,255,0.3)',
                  textAlign: 'center',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-4px)',
                    borderColor: 'rgba(255,255,255,0.5)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
                  }
                }}>
                  {place.icon}
                  <Typography sx={{ fontWeight: '500', fontSize: '15px' }}>{place.name}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* About Us Summary */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#003366', mb: 1 }}>About Us</Typography>
          <Typography sx={{ color: '#666', fontStyle: 'italic' }}>Improving menstrual hygiene with technology & care.</Typography>
        </Box>
        <Grid container spacing={4} justifyContent="center">
          {[
            { label: 'Our Vision', path: '/about#vision' },
            { label: 'Our Mission', path: '/about#mission' },
            { label: 'Founder', path: '/about#founder' }
          ].map((item, index) => (
            <Grid item key={index}>
              <Box
                component={Link} // Use the imported Link from react-router-dom
                to={item.path}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '240px',
                  height: '60px',
                  backgroundColor: '#fff',
                  borderTop: '4px solid #cc0000',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                  textDecoration: 'none',
                  color: '#333',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  borderRadius: '4px', // Slightly rounded
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 15px rgba(0,0,0,0.1)',
                    backgroundColor: '#fff',
                    color: '#cc0000' // change text color on hover
                  }
                }}
              >
                {item.label}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Our Impact */}
      <Box sx={{ backgroundColor: '#fff', py: 12, position: 'relative' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ textAlign: 'center', color: '#003366', fontWeight: 'bold', mb: 8, textTransform: 'uppercase', letterSpacing: '1px' }}>
            OUR IMPACT
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {[
              { label: 'Schools Covered', value: '100+' },
              { label: 'Women Benefited', value: '25,000+' },
              { label: 'Machines Installed', value: '500+' },
            ].map((stat, i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 5,
                    backgroundColor: '#fff',
                    borderRadius: '24px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.04)',
                    border: '1px solid #f0f0f0',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      background: 'linear-gradient(90deg, #7e57c2 0%, #5e35b1 100%)',
                    },
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 20px 50px rgba(126, 87, 194, 0.12)',
                      borderColor: '#e1bee7'
                    }
                  }}
                >
                  <Typography variant="h2" sx={{ 
                    fontWeight: '900', 
                    mb: 1.5, 
                    background: 'linear-gradient(45deg, #7e57c2 30%, #5e35b1 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    <AnimatedNumber value={stat.value} duration={2000 + (i * 500)} />
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: '#666', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '13px' }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Clients Section - Premium UI */}
      <Box sx={{ backgroundColor: '#fff', py: 10, position: 'relative', overflow: 'hidden' }}>
        {/* Subtle Background Decor */}
        <Box sx={{
          position: 'absolute',
          bottom: '-100px',
          left: '-100px',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(126,87,194,0.03) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 0
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box textAlign="center" mb={8}>
            <Typography variant="h6" sx={{ color: '#7e57c2', fontWeight: 'bold', mb: 1, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '14px' }}>
              Trusted Partners
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
              OUR CLIENTS
            </Typography>
            <Typography sx={{ color: '#666', mt: 2, maxWidth: '600px', mx: 'auto' }}>
              Trusted by leading organizations, hospitals, and educational institutions for sustainable hygiene solutions.
            </Typography>
          </Box>

          <Box className="marquee-container">
            <Box className="marquee-content">
              {(() => {
                const clientList = [
                  { name: 'Jayalakshmi Institute of Technology', img: require('../images/clients/WhatsApp Image 2026-04-13 at 1.29.29 PM.jpeg') },
                  { name: 'AYDA', img: require('../images/clients/WhatsApp Image 2026-02-06 at 8.14.48 PM (1).jpeg') },
                  { name: 'PLUS MAX', img: require('../images/clients/WhatsApp Image 2026-04-13 at 1.31.36 PM.jpeg') },
                  { name: 'Berlin Technovation', img: require('../images/clients/WhatsApp Image 2026-03-03 at 2.59.46 PM.jpeg') },
                  { name: 'Uzhavar Nala Mempattu Arakattalai', img: require('../images/clients/logo (1).png') },
                  { name: 'KK Groups', img: require('../images/clients/Picsart_25-11-25_15-32-58-349.png') },
                  { name: 'GrowAI TECH', img: require('../images/clients/growaitech.png') },
                  { name: 'Rudhra Construction', img: require('../images/clients/rudra.jpeg') },
                  { name: 'Sendur Builders', img: require('../images/clients/sendur.jpeg') },
                  { name: 'JTP', img: require('../images/clients/jtp.jpeg') },
                  { name: 'SKV Flex Printers', img: require('../images/clients/skv.jpeg') },
                  { name: 'New Client 1', img: require('../images/clients/WhatsApp Image 2026-09-27 at 2.45.25 PM.jpeg') },
                  { name: 'New Client 2', img: require('../images/clients/photo_2026-09-28_09-48-23.jpg') }
                ];
                return [...clientList, ...clientList].map((client, i) => (
                  <Box key={i} sx={{
                    width: '200px',
                    height: '140px',
                    borderRadius: '20px',
                    backgroundColor: '#fff',
                    border: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    margin: '0 15px',
                    flexShrink: 0,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 15px 30px rgba(126, 87, 194, 0.15)',
                      borderColor: '#e1bee7'
                    },
                    '&:hover img': {
                      filter: 'grayscale(0%)',
                      transform: 'scale(1.1)'
                    }
                  }}>
                    <Box
                      component="img"
                      src={client.img}
                      alt={client.name}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100px',
                        objectFit: 'contain',
                        filter: 'grayscale(100%)', // Default grayscale
                        transition: 'all 0.4s ease',
                        opacity: 0.8
                      }}
                    />
                  </Box>
                ));
              })()}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ backgroundColor: '#f9f9fc', py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ textAlign: 'center', mb: 6, fontWeight: 'bold', color: '#003366', textTransform: 'uppercase', letterSpacing: '1px' }}>
            WHAT OUR CLIENTS SAY
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {[
              {
                client: 'Jayalakshmi Institute of Technology',
                text: "The installation of Fem Tech Solution's smart vending machines has transformed our campus hygiene standards. It provides our female students with safe, dignified, and uninterrupted access to sanitary products."
              },
              {
                client: 'GrowAI TECH',
                text: "Partnering with Fem Tech has allowed us to ensure a truly inclusive workspace. Their disposal systems are eco-friendly and extremely reliable, perfectly aligning with our company's sustainability values."
              },
              {
                client: 'Berlin Technovation',
                text: "A seamless experience from installation to maintenance. The awareness programs conducted alongside the setup helped foster a healthier, more confident, and stigma-free environment for our team."
              }
            ].map((testimonial, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box sx={{
                  backgroundColor: '#fff',
                  p: 3.5,
                  borderRadius: '16px',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  border: '1px solid #f0f0f0',
                  '&:hover': { 
                    transform: 'translateY(-6px)', 
                    boxShadow: '0 15px 40px rgba(126, 87, 194, 0.1)',
                    borderColor: '#e1bee7'
                  }
                }}>
                  {/* Decorative Quote Icon watermark */}
                  <Typography sx={{
                    position: 'absolute',
                    top: '-5px',
                    right: '15px',
                    fontSize: '80px',
                    color: 'rgba(126, 87, 194, 0.06)',
                    fontFamily: 'serif',
                    lineHeight: 1,
                    zIndex: 0
                  }}>
                    "
                  </Typography>

                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography sx={{ 
                      color: '#555', 
                      fontStyle: 'italic', 
                      mb: 3, 
                      lineHeight: 1.7,
                      fontSize: '14px' 
                    }}>
                      "{testimonial.text}"
                    </Typography>
                  </Box>

                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mt: 'auto',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    {/* Avatar Circle with Initials */}
                    <Box sx={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: '#7e57c2',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '15px',
                      mr: 1.5,
                      boxShadow: '0 4px 10px rgba(126, 87, 194, 0.3)'
                    }}>
                      {testimonial.client.charAt(0)}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 'bold', color: '#333', fontSize: '14px' }}>
                        {testimonial.client}
                      </Typography>
                      <Typography sx={{ color: '#888', fontSize: '12px', mt: 0.2 }}>
                        Valued Partner
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

    </Box>
  );
}

export default Home;
